import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { translateText } from "../../translations/translationService";
import { notify } from "../../services/ui/notify";
import AppButton from "../Common/ui/AppButton";
import AppDialog from "../Common/ui/AppDialog";
import { SCREEN_IDS, backgroundColor2 } from "../../constants/Constants";
import { insertNewWeight, setLog } from "../../services/ConnectDataBase";
import { getCurrentDateTime, formatDate } from "../../services/Functions";
import { fetchForniturasPendientesForMo } from "../../services/utils/forniturasPesajeUtils";
import { FINDINGS_WEIGHING_SCREEN_COLORS } from "../../style/colors.js";

const TIPO_TIEMPO = "WEIGHT";
const TIPO_FORNITURA = "Fornitura.";

/**
 * Modal de pesaje de fornituras para una MO concreta (misma lógica que P. Fornituras).
 * Estado local: no modifica listaPesos de Redux.
 */
export default function ComponentPesajeForniturasMo({
  open,
  onClose,
  mo,
  lang,
  onAfterWeightRegistered,
}) {
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);
  const currentScreenId = SCREEN_IDS.FINDINGS_WEIGHT;

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [weights, setWeights] = useState({});
  const [registeringKey, setRegisteringKey] = useState(null);

  const loadRows = useCallback(async () => {
    if (!mo) return;
    setIsLoading(true);
    try {
      const moArray = await fetchForniturasPendientesForMo(mo);
      setRows(moArray);
      const initialWeights = {};
      moArray.forEach((item) => {
        initialWeights[item.key] = "";
      });
      setWeights(initialWeights);
    } catch (error) {
      console.error("Error cargando fornituras para MO:", error);
      notify.error(translateText("Error al cargar fornituras", lang));
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [mo, currentScreenId, lang]);

  useEffect(() => {
    if (open) {
      loadRows();
    } else {
      setRows([]);
      setWeights({});
      setRegisteringKey(null);
    }
  }, [open, loadRows]);

  const handleRegister = useCallback(
    async (item) => {
      const newWeightValue = weights[item.key];
      if (!newWeightValue || parseFloat(newWeightValue) === 0) {
        notify.warn(translateText("Introduzca un peso válido", lang));
        return;
      }

      setRegisteringKey(item.key);
      try {
        const status = await insertNewWeight(
          item.po,
          item.mo,
          usuario,
          equipo,
          item.codigo,
          newWeightValue,
          getCurrentDateTime(),
          TIPO_FORNITURA,
          TIPO_TIEMPO,
          currentScreenId,
          item.cantidad
        );

        if (!status) {
          notify.error(
            translateText("Error al añadir peso de fornitura", lang)
          );
          return;
        }

        const logData = `MO: ${item.mo}, Peso: ${newWeightValue}, Fecha y Hora: ${new Date().toLocaleString()}, Usuario: ${usuario}, Equipo: ${equipo}`;
        await setLog(SCREEN_IDS.GENERAL, "LOG", "PesajeFornituras", logData);

        setRows((prev) => prev.filter((r) => r.key !== item.key));
        setWeights((prev) => {
          const next = { ...prev };
          delete next[item.key];
          return next;
        });

        if (onAfterWeightRegistered) {
          await onAfterWeightRegistered();
        }

        notify.success(translateText("Peso registrado correctamente", lang));
      } catch (error) {
        console.error("Error registrando peso fornitura:", error);
        notify.error(translateText("Error al añadir peso de fornitura", lang));
      } finally {
        setRegisteringKey(null);
      }
    },
    [
      weights,
      usuario,
      equipo,
      currentScreenId,
      lang,
      onAfterWeightRegistered,
    ]
  );

  const headerSx = {
    fontWeight: 600,
    backgroundColor:
      FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
    borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
    fontSize: "0.75rem",
    color: "black",
    py: 1,
    px: 1,
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={`${translateText("Pesaje Fornituras", lang)} — ${mo || ""}`}
      actions={
        <AppButton onClick={onClose} variantType="secondary">
          {translateText("Cerrar", lang)}
        </AppButton>
      }
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" fontSize="0.9rem" py={2}>
          {translateText(
            "No hay líneas de fornitura pendientes de pesar para esta MO",
            lang
          )}
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 360 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={headerSx}>
                  {translateText("Referencia", lang)}
                </TableCell>
                <TableCell align="center" sx={headerSx}>
                  {translateText("Cantidad", lang)}
                </TableCell>
                <TableCell align="center" sx={headerSx}>
                  {translateText("gr. Teo. Uni", lang)}
                </TableCell>
                <TableCell align="center" sx={headerSx}>
                  {translateText("gr. Teo. Total", lang)}
                </TableCell>
                <TableCell align="center" sx={headerSx}>
                  {translateText("Entrega", lang)}
                </TableCell>
                <TableCell align="center" sx={headerSx}>
                  {translateText("Peso (g)", lang)}
                </TableCell>
                <TableCell align="center" sx={headerSx}>
                  {translateText("Acción", lang)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item) => (
                <TableRow key={item.key}>
                  <TableCell align="center" sx={{ fontSize: "0.8rem", py: 0.5 }}>
                    {item.codigo}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.8rem", py: 0.5 }}>
                    {Number(item.cantidad).toFixed(2)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.8rem", py: 0.5 }}>
                    {parseFloat(item.gramos || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.8rem", py: 0.5 }}>
                    {(parseFloat(item.gramos || 0) * parseFloat(item.cantidad || 0)).toFixed(2)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.8rem", py: 0.5 }}>
                    {formatDate(item.fechaentrega)}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <TextField
                      value={weights[item.key] || ""}
                      onChange={(e) =>
                        setWeights((prev) => ({
                          ...prev,
                          [item.key]: e.target.value,
                        }))
                      }
                      type="number"
                      placeholder={translateText("Peso", lang)}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 28,
                          width: 90,
                          fontSize: "0.75rem",
                        },
                        "& input": { textAlign: "center", py: 0.5 },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <AppButton
                      onClick={() => handleRegister(item)}
                      disabled={registeringKey === item.key}
                      sx={{
                        minWidth: 100,
                        fontSize: "0.75rem",
                        py: 0.25,
                        backgroundColor: backgroundColor2,
                      }}
                    >
                      {registeringKey === item.key ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        translateText("Registrar", lang)
                      )}
                    </AppButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AppDialog>
  );
}
